"use client";


import { cn } from "@/lib/utils";
import * as Radix from "@radix-ui/react-slider";
import * as Tooltip from "@radix-ui/react-tooltip";
import type { FormEvent, ForwardedRef, PropsWithChildren } from "react";
import { forwardRef, useEffect, useState } from "react";
import {
  exponentiateValue,
  exponentiateValues,
  localizeValues,
} from "./Slider.utils";

export type SliderProps = Omit<
  Radix.SliderProps,
  "onChange" | "value" | "onSubmit"
> &
  PropsWithChildren<{
    value?: number[];
    onChange?: (value: number[]) => void;
    onCommit?: (value: number[]) => void;
    onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
    tooltips?: "never" | "hover" | "always";
    uncontrolled?: boolean;
    exponentialScaling?: boolean;
    tooltip?: (value?: number) => string;
    classNames?: {
      root?: string;
      track?: string;
      range?: string;
      thumb?: string;
      tooltip?: string;
    };
  }>;

export const Slider = forwardRef(
  (props: SliderProps, ref: ForwardedRef<HTMLElement>) => {
    const {
      min = 1,
      max = 100,
      step = 0.1,
      value,
      defaultValue = [max / 2],
      tooltip = (v) => String(v),
      tooltips = "never",
      onChange,
      onCommit,
      onSubmit,
      children,
      classNames,
      uncontrolled = !onChange && !onCommit,
      exponentialScaling,
    } = props;
    const [suspended, setSuspended] = useState<boolean>(false);
    const [localValue, setLocalValue] = useState<number[]>(() => {
      if (exponentialScaling) {
        return localizeValues(value ?? defaultValue, min, max);
      }
      return value ?? defaultValue;
    });

    const onValueChange = (payload: number[]) => {
      setSuspended(true);
      setLocalValue(payload);
      if (exponentialScaling) {
        onChange?.(exponentiateValues(payload, min, max));
      } else {
        onChange?.(payload);
      }
    };

    const onValueCommit = (payload: number[]) => {
      setSuspended(false);
      if (exponentialScaling) {
        onCommit?.(exponentiateValues(payload, min, max));
      } else {
        onCommit?.(payload);
      }
    };

    useEffect(() => {
      if (value) {
        if (!uncontrolled || !suspended) {
          if (exponentialScaling) {
            setLocalValue(localizeValues(value, min, max));
          } else {
            setLocalValue(value ?? []);
          }
        }
      }
    }, [value, suspended, uncontrolled, exponentialScaling, min, max]);

    return (
      <form onSubmit={onSubmit}>
        <Radix.Root
          ref={ref}
          value={localValue}
          min={exponentialScaling ? 0 : min}
          max={exponentialScaling ? 100 : max}
          step={exponentialScaling ? 0.1 : step}
          minStepsBetweenThumbs={1}
          onValueChange={onValueChange}
          onValueCommit={onValueCommit}
          className={cn(
            "relative z-10 flex w-full touch-none select-none items-center",
            classNames?.root,
          )}
        >
          <Radix.Track className="relative flex-1 cursor-pointer py-2">
            <div
              className={cn(
                "relative h-px w-full overflow-hidden bg-gray-300",
                classNames?.track,
              )}
            >
              <Radix.Range
                className={cn("absolute h-full bg-black", classNames?.range)}
              />
            </div>
          </Radix.Track>
          {localValue.map((_, i) => (
            <Radix.Thumb
              key={i}
              className={cn("relative block", classNames?.thumb)}
            >
              <div className="h-3 w-3 bg-black shadow-sm" />
              {tooltips !== "never" ? (
                <Tooltip.Provider skipDelayDuration={300} delayDuration={300}>
                  <Tooltip.Root
                    {...(tooltips === "always" && {
                      open: true,
                      onOpenChange: () => null,
                    })}
                  >
                    <Tooltip.Trigger asChild>
                      <div className="absolute -left-2 -top-2 h-7 w-7 cursor-grab" />
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        side="top"
                        onPointerDownOutside={(e: any) => e.preventDefault()}
                        sideOffset={-4}
                        className={cn(
                          "bg-black/90 px-2 py-1 text-sm text-white shadow",
                          classNames?.tooltip,
                        )}
                      >
                        {tooltip(
                          exponentialScaling
                            ? exponentiateValue(min, max, localValue.at(i))
                            : localValue.at(i),
                        )}
                        <Tooltip.Arrow className="fill-black/90" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              ) : (
                <div className="absolute -left-2 -top-2 h-7 w-7 cursor-grab" />
              )}
            </Radix.Thumb>
          ))}
        </Radix.Root>
        {children}
      </form>
    );
  },
);

export default Slider;
